import { Injectable, signal } from '@angular/core';

export interface LocaleOption {
  value: string;
  label: string;
}

export interface TimezoneOption {
  value: string;
  label: string;
  /** Current UTC offset for the zone, e.g. "UTC-04:00" (DST-aware). */
  offset: string;
}

/** Locales the portal supports for date/time display. */
export const SUPPORTED_LOCALES: LocaleOption[] = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-IN', label: 'English (IN)' }
];

/** Time zones the portal supports (IANA ids — DST is applied automatically). */
const SUPPORTED_TIMEZONES: { value: string; label: string }[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'New York (ET)' },
  { value: 'America/Chicago', label: 'Chicago (CT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Kolkata', label: 'Kolkata (IST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }
];

/** Common IANA aliases browsers may report, mapped to our supported ids. */
const TIMEZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Etc/UTC': 'UTC',
  'Etc/GMT': 'UTC',
  'GMT': 'UTC',
  'Europe/Kiev': 'Europe/Paris'
};

export const DEFAULT_LOCALE = 'en-US';
/** Eastern Time — the portal's default reporting zone. */
export const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * The zone's UTC offset at `at`, e.g. "UTC-04:00". Resolved through Intl, so it
 * reflects whether daylight saving time is in effect on that date.
 */
export function utcOffset(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' }).formatToParts(at);
    const raw = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    // Zero-offset zones report a bare "GMT" rather than "GMT+00:00".
    if (!raw || raw === 'GMT') {
      return 'UTC+00:00';
    }
    return raw.replace('GMT', 'UTC');
  } catch {
    return 'UTC+00:00';
  }
}

/**
 * The zone's UTC offset as a bare signed value at `at`, e.g. "-04:00" — the
 * form APIs expect. DST-aware, so pass the date the offset should apply to.
 */
export function utcOffsetValue(timeZone: string, at: Date = new Date()): string {
  return utcOffset(timeZone, at).replace('UTC', '');
}

/** The browser's locale, if we support it; otherwise the default. */
function detectLocale(): string {
  const candidates = [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const candidate of candidates) {
    const match = SUPPORTED_LOCALES.find(l => l.value.toLowerCase() === candidate.toLowerCase());
    if (match) {
      return match.value;
    }
  }
  return DEFAULT_LOCALE;
}

/** The browser's time zone, if we support it; otherwise the default (ET). */
function detectTimezone(): string {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zone = TIMEZONE_ALIASES[resolved] ?? resolved;
    return SUPPORTED_TIMEZONES.some(t => t.value === zone) ? zone : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Holds user-selected display preferences (locale + time zone) used to
 * format dates/times throughout the app. Components read these signals so
 * their formatting reacts automatically when the user changes a setting.
 *
 * On init the browser's locale and time zone are detected and pre-selected
 * when supported, falling back to English (US) and Eastern Time. All date
 * formatting goes through Intl with an IANA zone id, so daylight saving is
 * applied automatically for the timestamp being formatted.
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  /** Locale options for the toolbar dropdown. */
  readonly locales: LocaleOption[] = SUPPORTED_LOCALES;

  /** Time-zone options, each labelled with its current UTC offset. */
  readonly timezones: TimezoneOption[] = SUPPORTED_TIMEZONES.map(t => ({
    ...t,
    offset: utcOffset(t.value)
  }));

  /** BCP-47 locale used to format displayed dates. */
  /* readonly locale = signal<string>(detectLocale()); */
  readonly locale = signal<string>(DEFAULT_LOCALE);

  /** IANA time zone used to display timestamps. */
  readonly timezone = signal<string>(detectTimezone());

  defaultTimezoneInitialized: boolean = false;

  setLocale(locale: string): void {
    this.locale.set(locale);
  }

  setTimezone(timezone: string): void {
    this.timezone.set(timezone);
  }
}
